import React from 'react';
import PropTypes from 'prop-types';
import * as Geocoder from '@mapbox/mapbox-gl-geocoder';

import SearchComponent from 'components/SearchComponent';
import {
  MAPBOX_GEOCODER_ID,
  MAPBOX_GEOCODER_INPUT_ID,
  MAPBOX_GEOCODER_INPUT_CLASS,
  MAPBOX_GEOCODER_INPUT_PLACEHOLDER,
} from 'constants/mapbox';

class SearchContainer extends React.Component {
  state = {
    isSearchUsable: false,
  };

  componentDidMount() {
    this.setupSearch();
  }

  componentWillUnmount() {
    this.geocoder.off('result', this.handleResult);
  }

  initializeGeocoder() {
    const options = {
      accessToken: this.props.mapboxAccessToken,
      placeholder: MAPBOX_GEOCODER_INPUT_PLACEHOLDER,
      trackProximity: false,
      types:
        'address, postcode, region, district, locality, neighborhood, place',
    };

    const geocoder = new Geocoder(options);

    return geocoder;
  }

  setupSearch = () => {
    const geocoder = this.initializeGeocoder();

    geocoder.on('result', this.handleResult);

    // addTo will throw if it can't add the geocoder to the DOM
    // or the accessToken provided isn't valid so this is the
    // best opportunity to bail
    try {
      geocoder.addTo(MAPBOX_GEOCODER_ID);
    } catch (error) {
      console.error(error);
      return;
    }

    // a11y is sometimes hacky; add an id so the input is connected to a label
    geocoder._inputEl.setAttribute('id', MAPBOX_GEOCODER_INPUT_ID);
    this.geocoder = geocoder;

    this.setState({ isSearchUsable: true });
  };

  /**
   * Accept a result object and return an object with the correct shape
   */
  searchResultPayload = (result) => {
    const { geometry } = result;
    const [lng, lat] = geometry.coordinates;

    const searchResult = {
      geometry: {
        location: {
          lat: () => lat,
          lng: () => lng,
        },
      },
    };

    return searchResult;
  };

  handleResult = ({ result }) => {
    const { updateSearchResults } = this.props;

    const searchResult = this.searchResultPayload(result);
    updateSearchResults(searchResult);
  };

  handleClear = (event) => {
    const { revertToDefault } = this.props;

    // this isn't ideal but we're using `_clear` because `clear` applies
    // a focus to the input and there isn't a cleaner way to disable that
    this.geocoder._clear(event);

    revertToDefault();
  };

  manualSearch = () => {
    const selector = `${MAPBOX_GEOCODER_ID} ${MAPBOX_GEOCODER_INPUT_CLASS}`;
    const input = document.querySelector(selector).value.trim();

    const isInputInvalid =
      input === '' || input.length <= this.geocoder.getMinLength();

    if (isInputInvalid) {
      return;
    }

    this.geocoder.query(input);
  };

  render() {
    return (
      <SearchComponent
        handleClear={this.handleClear}
        handleManualSearch={this.manualSearch}
        locationNearby={this.props.locationNearby}
        isSearchUsable={this.state.isSearchUsable}
      />
    );
  }
}

SearchContainer.propTypes = {
  googleMapURL: PropTypes.string.isRequired,
  mapboxAccessToken: PropTypes.string.isRequired,
  locations: PropTypes.arrayOf(PropTypes.object).isRequired,
  revertToDefault: PropTypes.func.isRequired,
  updateCenter: PropTypes.func.isRequired,
  updateSearchResults: PropTypes.func.isRequired,
  updateZoom: PropTypes.func.isRequired,
  locationNearby: PropTypes.bool.isRequired,
};

export default SearchContainer;
